import React from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { BaseNavigationProps } from '../../navigation/_root'
import {
  ATLButton,
  ATLSpacing,
  ATLTextBody,
  ATLTextHead,
  SpacingSizesEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { CustomHeader } from '../../components/CustomHeader'
import { atlTheme } from '../../atlTheme'
import { messages } from '../../constants/strings/messages'
import { PASSWORD_CONFIRMATION_CONSTRAINT, PASSWORD_CONSTRAINT } from '../../constants/formsConstraints'
import { useForm } from 'react-simple-hooks-forms/dist'
import { ATLTextInputField } from '../../components/form/ATLTextInputField'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import styled from 'styled-components/native'
import { usePromise } from '../../utils/hooks/usePromise'
import { useGlobal } from '../../global/useGlobal'
import { showErrorSnackbar, showMessageSnackbar } from '../../components/snackbar/Snackbar'
import { Loading } from '../../components/loading/Loading'
import { Colors } from '../../constants/colors'
import validate from 'validate.js'
import { ChangePasswordInput } from '../../services/GlobalTypes'
import { changePassword } from '../../services/auth/changePassword'
import { FeatureManager } from '../../FeatureManager'

const RedefinePasswordContainer = styled.SafeAreaView`
  background-color: ${atlTheme.ColorSurface};
  height: 100%;
`
const ButtonContainer = styled.View`
  position: absolute;
  width: 100%;
  left: 0px;
  top: 80%;
`
const LoadingButtonContainer = styled.View`
  margin-vertical: ${atlTheme.SpacingBase}px;
  margin-horizontal: ${atlTheme.SpacingDistribute}px;
  height: ${atlTheme.HeightButtonBase}px;
  background-color: ${atlTheme.ColorBrand1Default};
  border-radius: ${atlTheme.BorderRadiusFloatingButton}px;
  justify-content: center;
`

const formName = 'RedefinePassword'

const formFields = {
  NEW_PASSWORD: 'newPassword',
  NEW_PASSWORD_AGAIN: 'newPasswordAgain',
}
const constraints = {
  [formFields.NEW_PASSWORD]: PASSWORD_CONSTRAINT,
  [formFields.NEW_PASSWORD_AGAIN]: PASSWORD_CONFIRMATION_CONSTRAINT,
}
const validator = (constraints: any) => (values: any) => {
  if (values.newPassword !== values.newPasswordAgain) {
    showErrorSnackbar(formatMessage(messages.redefinePassword_snackbar_differentPasswords))
  }
  return validate(values, constraints)
}

export const RedefinePasswordScreen: NavigationStackScreenComponent<BaseNavigationProps> = () => {
  const { Form, submit } = useForm({ validator: validator(constraints), formName })
  const [oldPassword] = useGlobal('passwordOnFirstLogin')

  const onRequestSuccess = () => {
    showMessageSnackbar(formatMessage(messages.redefinePassword_snackbar_success))
    FeatureManager.isOnboardingEnabled ? Coordinator.goToOnboarding() : Coordinator.goToMainScreen()
  }

  const onRequestError = () => {
    showErrorSnackbar(formatMessage(messages.error_passwordRedefinition))
  }

  const { loading: isChangingPassword, request: doChangePassword } = usePromise(changePassword, [], {
    onSuccess: onRequestSuccess,
    onError: onRequestError,
  })

  const onSuccess = (val: ChangePasswordInput) => {
    doChangePassword({ oldPassword: oldPassword, newPassword: val.newPassword })
  }

  const onError = err => {
    console.tron.log('Error while validating change password: ', err)
  }

  const onPressSubmit = async () => {
    await submit(onSuccess, onError)
  }

  return (
    <Form>
      <ATLSpacing type={SpacingSizesEnum.marginal} />
      <RedefinePasswordContainer>
        <ATLSpacing type={SpacingSizesEnum.marginal} />
        <ATLTextHead
          textAlignment={TextAlignmentEnum.left}
          labelBaseProps={{
            fontSize: atlTheme.FontSizeHighlight,
          }}
        >
          {formatMessage(messages.redefinePassword_newPassword)}
        </ATLTextHead>
        <ATLTextBody labelBaseProps={{ multilineLimit: 1 }}>
          {formatMessage(messages.redefinePassword_newPasswordAgain)}
        </ATLTextBody>
        <ATLSpacing type={SpacingSizesEnum.marginal} />
        <ATLTextInputField
          name={formFields.NEW_PASSWORD}
          title={formatMessage(messages.redefinePassword_newPassword)}
          horizontalMargin={atlTheme.SpacingMarginal}
          textInputBaseProps={{
            focusColor: atlTheme.ColorMediumContrast,
          }}
          showVisibilityToggle
        />
        <ATLSpacing type={SpacingSizesEnum.distribute} />
        <ATLTextInputField
          name={formFields.NEW_PASSWORD_AGAIN}
          title={formatMessage(messages.redefinePassword_newPasswordAgain)}
          horizontalMargin={atlTheme.SpacingMarginal}
          textInputBaseProps={{
            focusColor: atlTheme.ColorMediumContrast,
          }}
          showVisibilityToggle
        />
        <ButtonContainer>
          {isChangingPassword ? (
            <LoadingButtonContainer>
              <Loading color={Colors.white} size={'small'} />
            </LoadingButtonContainer>
          ) : (
            <ATLButton
              title={formatMessage(messages.redefinePassword_changePassword)}
              onPress={onPressSubmit}
              buttonBaseProps={{ borderRadius: atlTheme.BorderRadiusFloatingButton }}
            />
          )}
        </ButtonContainer>
      </RedefinePasswordContainer>
    </Form>
  )
}
RedefinePasswordScreen.navigationOptions = CustomHeader(
  formatMessage(messages.redefinePassword_headerTitle),
  true,
  () => Coordinator.goBack()
)
